import { useMemo } from "react";

import type { CalendarEvent } from "../../planner/api/types/calendar";
import { useTodaysVideoEvents } from "./useTodaysVideoEvents";

/**
 * US 12.2 — the calendar event this call belongs to, if any. Calls carry no
 * event link of their own, so the event is found by its video link naming
 * this room (its id, slug or join code). Returns null for an ad-hoc call.
 */
export function matchCallEvent(events: CalendarEvent[], identifiers: (string | null | undefined)[]): CalendarEvent | null {
  const ids = identifiers.filter((x): x is string => !!x && x.length >= 4).map((x) => x.toLowerCase());
  if (!ids.length) return null;
  const now = Date.now();
  const matches = events.filter((e) => {
    const link = (e.video_link ?? "").toLowerCase();
    return ids.some((id) => new RegExp(`(^|[/=?&#])${id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|[/?&#])`).test(link));
  });
  // A room reused by several events today: prefer the one running now.
  return (
    matches.find((e) => new Date(e.start).getTime() <= now && new Date(e.end).getTime() > now) ?? matches[0] ?? null
  );
}

export function useCallCalendarEvent(identifiers: (string | null | undefined)[]): CalendarEvent | null {
  const { events } = useTodaysVideoEvents();
  const key = identifiers.join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => matchCallEvent(events, identifiers), [events, key]);
}
